<div class="space-y-5">
  @if (isDekalbUser() && stats(); as dashboardStats) {
    <section class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="text-sm text-slate-500">Clients Served</div>
        <div class="mt-2 text-3xl font-semibold text-slate-900">{{ dashboardStats.clientsServed }}</div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="text-sm text-slate-500">Open Cases</div>
        <div class="mt-2 text-3xl font-semibold text-slate-900">{{ dashboardStats.openCases }}</div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="text-sm text-slate-500">To Be Closed</div>
        <div class="mt-2 text-3xl font-semibold text-slate-900">{{ dashboardStats.toBeClosedCount }}</div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="text-sm text-slate-500">Violent Incident</div>
        <div class="mt-2 text-3xl font-semibold text-slate-900">{{ dashboardStats.violentIncidentCount }}</div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="text-sm text-slate-500">Top Service</div>
        <div class="mt-2 text-sm font-semibold text-slate-900">
          @if (dashboardStats.topService) {
            {{ dashboardStats.topService.serviceName }} ({{ dashboardStats.topService.caseCount }})
          } @else {
            N/A
          }
        </div>
      </div>
    </section>

    <section class="grid gap-4 lg:grid-cols-2">
      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 class="text-base font-semibold text-slate-900">Top 5 Zip Codes</h2>
        <div class="mt-4 space-y-3">
          @for (zip of dashboardStats.zipCodesTop5; track zip.zipCode) {
            <div>
              <div class="mb-1 flex items-center justify-between text-sm text-slate-700">
                <span>{{ zip.zipCode }}</span>
                <span>{{ zip.caseCount }}</span>
              </div>
              <div class="h-2 rounded-full bg-slate-100">
                <div class="h-2 rounded-full bg-slate-700" [style.width.%]="zipBarWidth(zip.caseCount)"></div>
              </div>
            </div>
          } @empty {
            <div class="text-sm text-slate-500">No zip code data available.</div>
          }
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 class="text-base font-semibold text-slate-900">Age Range / Race</h2>
        <div class="mt-4 space-y-4">
          @for (ageRange of dashboardStats.ageRangeRace; track ageRange.ageRange) {
            <div class="rounded-xl border border-slate-200 p-3">
              <div class="text-sm font-semibold text-slate-800">{{ ageRange.ageRange }}</div>
              <div class="mt-2 space-y-2">
                @for (race of ageRange.races; track race.race) {
                  <div>
                    <div class="mb-1 flex items-center justify-between text-xs text-slate-600">
                      <span>{{ race.race }}</span>
                      <span>{{ race.caseCount }}</span>
                    </div>
                    <div class="h-2 rounded-full bg-slate-100">
                      <div
                        class="h-2 rounded-full bg-emerald-600"
                        [style.width.%]="raceBarWidth(race.caseCount, ageRange.races)"
                      ></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          } @empty {
            <div class="text-sm text-slate-500">No age/race data available.</div>
          }
        </div>
      </div>
    </section>
  }

  <section class="rounded-2xl border border-emerald-200 p-4 shadow-sm sm:p-5">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-base font-semibold text-emerald-900">New Client Onboarding</h2>
      </div>

      <a
        routerLink="/clients/initial-contact"
        class="rounded-xl border border-emerald-700 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
      >
        Initial Contact
      </a>
    </div>
  </section>

  <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-lg font-semibold text-slate-900">Case Worklist</h1>
          <p class="text-sm text-slate-500">
            Showing {{ totalCases() }} cases across {{ totalClients() }} clients.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          @if (canUseToBeClosed()) {
            <button
              type="button"
              (click)="setToBeClosedFilter()"
              [attr.aria-pressed]="filters().toBeClosedOnly"
              class="filter-chip rounded-xl border px-4 py-2 text-sm font-semibold"
              [class.filter-chip-warning-selected]="filters().toBeClosedOnly"
            >
              To Be Closed ({{ toBeClosedCount() }})
            </button>
          }
        </div>
      </div>

      <div class="grid gap-3 lg:grid-cols-[minmax(18rem,28rem)_16rem_7rem]">
        <input
          type="text"
          [ngModel]="draftFilters().search"
          (ngModelChange)="setDraftSearch($event)"
          [ngModelOptions]="{ standalone: true }"
          (keydown.enter)="runClientSearch()"
          placeholder="Search for client"
          class="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
        />

        <select
          [ngModel]="draftFilters().partnerOrganizationId"
          (ngModelChange)="setDraftPartnerOrganizationIdFromModel($event)"
          [ngModelOptions]="{ standalone: true }"
          [disabled]="role() === 'partner' || loadingPartners()"
          class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option [ngValue]="null">All Partners</option>
          @for (partner of partners(); track partner.id) {
            <option [ngValue]="partner.id">{{ partner.name }}</option>
          }
        </select>

        <button
          type="button"
          (click)="runClientSearch()"
          class="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Search
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          (click)="setStatusFilter('all')"
          class="filter-chip rounded-full border px-3 py-1.5 text-xs font-semibold"
          [class.filter-chip-selected]="filters().status === 'all' && !filters().toBeClosedOnly"
        >
          All
        </button>

        <button
          type="button"
          (click)="setStatusFilter('open')"
          class="filter-chip rounded-full border px-3 py-1.5 text-xs font-semibold"
          [class.filter-chip-selected]="filters().status === 'open' && !filters().toBeClosedOnly"
        >
          Open
        </button>

        <button
          type="button"
          (click)="setStatusFilter('closed')"
          class="filter-chip rounded-full border px-3 py-1.5 text-xs font-semibold"
          [class.filter-chip-selected]="filters().status === 'closed' && !filters().toBeClosedOnly"
        >
          Closed
        </button>
      </div>
    </div>

    <div class="mt-5">
      @if (errorMessage()) {
        <div class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {{ errorMessage() }}
        </div>
      } @else if (loading()) {
        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          Loading live case data...
        </div>
      } @else if (groupedCaseCards().length === 0) {
        <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <div class="text-base font-semibold text-slate-900">No cases found for the current filters.</div>
          <p class="mt-2 text-sm text-slate-600">Adjust your filter selections to broaden results.</p>
        </div>
      } @else {
        <div class="space-y-5">
          @for (group of groupedCaseCards(); track trackGroupByClientId($index, group)) {
            <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <div class="flex min-w-0 flex-wrap items-center gap-2">
                  <a
                    [routerLink]="['/clients', group.clientId]"
                    class="text-lg font-semibold text-slate-900 hover:underline"
                  >
                    {{ group.clientName }}
                  </a>
                  <span
                    class="rounded-full px-2.5 py-1 text-xs font-semibold"
                    [class.bg-emerald-100]="group.clientStatus === 'open'"
                    [class.text-emerald-700]="group.clientStatus === 'open'"
                    [class.bg-slate-200]="group.clientStatus === 'closed'"
                    [class.text-slate-700]="group.clientStatus === 'closed'"
                  >
                    Client {{ group.clientStatus === 'open' ? 'Open' : 'Closed' }}
                  </span>
                  @if (group.birthdate) {
                    <span class="text-xs text-slate-500">DOB: {{ group.birthdate | date:'MMM d, y':'UTC' }}</span>
                  }
                </div>

                <a
                  [routerLink]="['/clients', group.clientId]"
                  class="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  View Client Page
                </a>
              </div>

              @if (group.initialContactReason) {
                <div class="mb-4 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                  <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason for Initial Contact</h3>
                  <p class="mt-1 text-sm leading-relaxed text-slate-700">
                    {{ group.initialContactReason }}
                  </p>
                </div>
              }

              <div class="space-y-4">
                @for (item of group.caseCards; track item.id) {
                  <app-case-card [caseItem]="item" />
                }
              </div>
            </section>
          }
        </div>
      }
    </div>
  </section>
</div>
